import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth, formatErr } from "../context/AuthContext";
import Navbar from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card } from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export default function Login() {
  const { login, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const u = await login(email, password, remember);
      toast.success("Accesso effettuato");
      if (u.must_change_password) navigate("/change-password", { replace: true });
      else if (u.role === "admin" || u.role === "personale") navigate("/admin", { replace: true });
      else navigate(location.state?.from || "/dashboard", { replace: true });
    } catch (e) {
      setErr(formatErr(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar/>
      <div className="max-w-md mx-auto pt-16 px-4">
        <Card className="p-8">
          <div className="flex items-center gap-3 mb-6">
            <img src="/assets/logo.png" alt="Energy World" className="w-12 h-12 rounded-full"/>
            <div>
              <h1 className="heading text-2xl font-extrabold text-slate-900">Accedi</h1>
              <p className="text-sm text-slate-500">Bentornato in Energy World</p>
            </div>
          </div>
          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-600 mb-1 block">Email</label>
              <Input type="email" required value={email} onChange={e=>setEmail(e.target.value)} autoComplete="username" data-testid="login-email"/>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-600 mb-1 block">Password</label>
              <Input type="password" required value={password} onChange={e=>setPassword(e.target.value)} autoComplete="current-password" data-testid="login-password"/>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer select-none">
              <Checkbox checked={remember} onCheckedChange={setRemember} data-testid="login-remember"/>
              Ricordami per 30 giorni
            </label>
            {err && <div className="text-sm text-red-600 bg-red-50 rounded p-2" data-testid="login-error">{err}</div>}
            <Button type="submit" disabled={busy} className="w-full bg-blue-800 hover:bg-blue-900" data-testid="login-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : "Accedi"}
            </Button>
          </form>

          <div className="my-4 flex items-center gap-3 text-xs text-slate-400 uppercase tracking-widest">
            <div className="flex-1 h-px bg-slate-200"/>oppure<div className="flex-1 h-px bg-slate-200"/>
          </div>
          <Button type="button" variant="outline" onClick={loginWithGoogle} className="w-full border-slate-300 hover:bg-slate-50 font-medium" data-testid="login-google-btn">
            <svg className="w-5 h-5 mr-2" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
            Continua con Google
          </Button>
          <div className="mt-4 text-center text-sm text-slate-600">
            Non hai un account? <Link to="/register" className="text-blue-800 font-semibold" data-testid="login-register-link">Registrati</Link>
          </div>
          <div className="mt-2 text-center text-sm">
            <Link to="/forgot-password" className="text-slate-500 hover:text-blue-800" data-testid="login-forgot-link">Password dimenticata?</Link>
          </div>
        </Card>
      </div>
    </div>
  );
}
